import React, { useState } from 'react';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Platform } from '../../../types/Platforms';
import { cn } from '../../../lib/utils';
import { primaryText, semiBoldText, largeTextSize } from '../../../styling/stylingHelper';
import { InvoiceDataComponent } from './InvoiceDataComponent';
import { InvoiceService } from '../../../services/invoiceService';
import { SingleExtractionResponse } from '../../../types/invoiceTypes';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';

const TestInvoicesPage: React.FC = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | ''>(Platform.ANY_INVOICE);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<SingleExtractionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get restaurant ID from store
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  // Get all platform values (filter out namespace methods)
  const platforms = Object.values(Platform).filter(v => typeof v === 'string').sort() as Platform[];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlatform || !selectedFile) {
      setError('Please select both a platform and a file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const data = await InvoiceService.extractSingleInvoice(
        selectedPlatform,
        selectedFile,
        restaurantId
      );
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while processing the invoice');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="h-full p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={cn(primaryText, semiBoldText, largeTextSize, "mb-2")}>
            Invoice Testing
          </h1>
          <p className={cn(primaryText, "text-sm opacity-70")}>
            Test invoice extraction for different platforms
          </p>
        </div>

        {/* Form */}
        <div className="bg-backgroundLight dark:bg-backgroundLight-dark rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Platform Selection */}
          <div className="mb-6">
            <label className={cn(primaryText, semiBoldText, "block mb-2")}>
              Select Platform
            </label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value as Platform)}
              className="w-full px-4 py-2 rounded-md bg-background dark:bg-background-dark border border-gray-300 dark:border-gray-600 text-primaryText dark:text-primaryText-dark focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark"
            >
              <option value="">Choose a platform...</option>
              {platforms.map((platform) => (
                <option key={platform} value={platform}>
                  {platform.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className={cn(primaryText, semiBoldText, "block mb-2")}>
              Upload Invoice File
            </label>
            <div className="flex items-center gap-4">
              <label className="flex-1 flex items-center justify-center px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-accent dark:hover:border-accent-dark transition-colors">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                  <span className={cn(primaryText, "text-sm")}>
                    {selectedFile ? selectedFile.name : 'Click to upload or drag and drop'}
                  </span>
                  <p className={cn(primaryText, "text-xs opacity-60 mt-1")}>
                    PDF, PNG, JPG up to 10MB
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                />
              </label>
            </div>
            {selectedFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <FileText className="h-4 w-4" />
                <span>{selectedFile.name}</span>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!selectedPlatform || !selectedFile || isProcessing}
            className="w-full px-6 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                Extract Invoice Data
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className={cn(primaryText, semiBoldText, "text-red-800 dark:text-red-300 mb-1")}>
                Error
              </h3>
              <p className={cn(primaryText, "text-sm text-red-700 dark:text-red-400")}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Result Display */}
        {result && (
          <div className="mt-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className={cn(primaryText, semiBoldText, "text-green-800 dark:text-green-300 mb-1")}>
                  Extraction Successful
                </h3>
                <p className={cn(primaryText, "text-sm text-green-700 dark:text-green-400")}>
                  Platform: {result.platform} | Duration: {result.duration.toFixed(2)}s | Costs: €{(result.costs * 100).toFixed(2)} cents
                </p>
              </div>
            </div>

            {/* Visual Display */}
            <div className="mt-4">
              <InvoiceDataComponent
                orderData={result.orderData}
                platform={result.platform}
              />
            </div>

            {/* Raw JSON Data - Collapsible */}
            <details className="mt-4">
              <summary className={cn(primaryText, semiBoldText, "cursor-pointer hover:text-accent dark:hover:text-accent-dark py-2")}>
                View Raw JSON Data
              </summary>
              <div className="bg-background dark:bg-background-dark rounded-md p-4 mt-2">
                <pre className="text-xs text-primaryText dark:text-primaryText-dark overflow-x-auto bg-backgroundDarker dark:bg-backgroundDarker-dark p-4 rounded">
                  {JSON.stringify(result.orderData, null, 2)}
                </pre>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestInvoicesPage;
